import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchRevenueAnalytics,
  fetchSalesAnalytics,
  fetchProductsAnalytics,
  fetchPaymentMethodsAnalytics,
  fetchBranchAnalytics,
  fetchStaffAnalytics,
  fetchCustomerAnalytics,
  fetchProductCategoriesAnalytics,
} from "../../api/analytics";

const initialState = {
  loading: false,
  loaded: false,
  revenueAnalytics: {},
  salesAnalytics: {},
  productsAnalytics: {},
  categoriesAnalytics: {},
  paymentMethodsAnalytics: {},
  branchAnalytics: {},
  staffAnalytics: {},
  customerAnalytics: {},
  error: null,
  lastParams: {
    filter_type: "last_30_days",
    branch_id: "none",
    start_date: "",
    end_date: "",
  },
};

export const loadAnalytics = createAsyncThunk(
  "analytics/loadAnalytics",
  async (
    { organization_id, branch_id, filter_type, start_date, end_date },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState().analyticsReducer;

      const effectiveFilterType = filter_type || state.lastParams.filter_type;
      const effectiveBranchId = branch_id || state.lastParams.branch_id;
      const effectiveStartDate =
        start_date === "" || start_date
          ? start_date
          : state.lastParams.start_date;
      const effectiveEndDate =
        end_date === "" || end_date ? end_date : state.lastParams.end_date;

      const [
        revenue,
        sales,
        products,
        categories,
        paymentMethods,
        branches,
        staffs,
        customers,
      ] = await Promise.all([
        fetchRevenueAnalytics(
          organization_id,
          effectiveBranchId,
          effectiveFilterType,
          effectiveStartDate,
          effectiveEndDate
        ),
        fetchSalesAnalytics(
          organization_id,
          effectiveBranchId,
          effectiveFilterType,
          effectiveStartDate,
          effectiveEndDate
        ),
        fetchProductsAnalytics(
          organization_id,
          effectiveBranchId,
          effectiveFilterType,
          effectiveStartDate,
          effectiveEndDate
        ),
        fetchProductCategoriesAnalytics(
          organization_id,
          effectiveBranchId,
          effectiveFilterType,
          effectiveStartDate,
          effectiveEndDate
        ),
        fetchPaymentMethodsAnalytics(
          organization_id,
          effectiveBranchId,
          effectiveFilterType,
          effectiveStartDate,
          effectiveEndDate
        ),
        fetchBranchAnalytics(
          organization_id,
          effectiveFilterType,
          effectiveStartDate,
          effectiveEndDate
        ),
        fetchStaffAnalytics(
          organization_id,
          effectiveBranchId,
          effectiveFilterType,
          effectiveStartDate,
          effectiveEndDate
        ),
        fetchCustomerAnalytics(
          organization_id,
          effectiveBranchId,
          effectiveFilterType,
          effectiveStartDate,
          effectiveEndDate
        ),
      ]);

      return {
        revenue: revenue.data,
        sales: sales.data,
        products: products.data,
        categories: categories.data,
        paymentMethods: paymentMethods.data,
        branches: branches.data,
        staffs: staffs.data,
        customers: customers.data,
        params: {
          filter_type: effectiveFilterType,
          organization_id,
          branch_id: effectiveBranchId,
          start_date: effectiveStartDate,
          end_date: effectiveEndDate,
        },
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch analytics"
      );
    }
  }
);

export const loadAnalyticsIfNeeded = createAsyncThunk(
  "analytics/loadAnalyticsIfNeeded",
  async (
    { organization_id, branch_id, filter_type, start_date, end_date },
    { getState, dispatch }
  ) => {
    const state = getState().analyticsReducer;

    const currentParams = {
      filter_type,
      organization_id,
      branch_id,
      start_date,
      end_date,
    };

    const previousParams = {
      filter_type: state.lastParams?.filter_type,
      organization_id: state.lastParams?.organization_id,
      branch_id: state.lastParams?.branch_id,
      start_date: state.lastParams?.start_date,
      end_date: state.lastParams?.end_date,
    };

    if (JSON.stringify(currentParams) === JSON.stringify(previousParams)) {
      return;
    }

    return dispatch(
      loadAnalytics({
        organization_id,
        branch_id,
        filter_type,
        start_date,
        end_date,
      })
    );
  }
);

const analytics = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    clearAnalytics: () => {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.revenueAnalytics = action.payload.revenue;
        state.salesAnalytics = action.payload.sales;
        state.productsAnalytics = action.payload.products;
        state.paymentMethodsAnalytics = action.payload.paymentMethods;
        state.branchAnalytics = action.payload.branches;
        state.staffAnalytics = action.payload.staffs;
        state.customerAnalytics = action.payload.customers;
        state.categoriesAnalytics = action.payload.categories;
        state.lastParams = {
          ...state.lastParams,
          ...action.payload.params,
        };
      })
      .addCase(loadAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearAnalytics } = analytics.actions;

export default analytics.reducer;
